import React from 'react'
import {
  getMfeOverrides,
  setMfeOverride,
  clearMfeOverride,
  clearAllMfeOverrides,
  isDevServerRunning,
  getMfeProcesses,
  startMfeProcess,
  stopMfeProcess,
  openInVSCode,
  openInTerminal,
  getInstalledTerminals,
  pollMfeStatus,
  getMfeConfig,
  setMfeRoot,
  MfeProcessStatus,
  TerminalApp,
} from 'properties-ui-common'
import {
  Button,
  Card,
  Input,
  Badge,
  Heading,
  Tooltip,
} from '@design-systems/canvas-ui'

interface MfeConfig {
  name: string
  prodUrl: string
  localPort: number
  isLocal: boolean
}

interface MfeDevManagerProps {
  microFrontEnds: Record<string, string>
  startingPort?: number
  onConfigChange?: () => void
  isVisible?: boolean
}

const containerStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: '20px',
  right: '20px',
  width: '540px',
  maxHeight: '80vh',
  zIndex: 99999,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.1)',
  borderRadius: '16px',
}

// Status indicator for MFE processes
const StatusDot: React.FC<{ status: string }> = ({ status }) => {
  const labels: Record<string, string> = {
    running: '🟢 Running',
    starting: '🟡 Starting...',
    error: '🔴 Error',
    stopped: '⚪ Stopped',
  }
  return (
    <span style={{ fontSize: '12px', fontWeight: 500 }}>
      {labels[status] || status}
    </span>
  )
}

const MFE_PORT_CONFIGS_KEY = 'MFE_PORT_CONFIGS'

// Helper to get port configurations from localStorage
const getPortConfigs = (): Record<string, number> => {
  try {
    const stored = localStorage.getItem(MFE_PORT_CONFIGS_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

// Helper to save port configuration
const savePortConfig = (name: string, port: number): void => {
  const configs = getPortConfigs()
  if (port > 0) {
    configs[name] = port
  } else {
    delete configs[name]
  }
  localStorage.setItem(MFE_PORT_CONFIGS_KEY, JSON.stringify(configs))
}

/**
 * MFE Dev Manager - A visual UI for managing local MFE development overrides
 * Only visible on localhost for development purposes
 */
export const MfeDevManager: React.FC<MfeDevManagerProps> = ({
  microFrontEnds,
  startingPort = 8080,
  onConfigChange,
  isVisible = true,
}) => {
  const [configs, setConfigs] = React.useState<MfeConfig[]>([])
  const [hasChanges, setHasChanges] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')
  const [showActiveOnly, setShowActiveOnly] = React.useState(false)

  // Dev server state
  const [serverConnected, setServerConnected] = React.useState(false)
  const [processStatuses, setProcessStatuses] = React.useState<
    Record<string, MfeProcessStatus>
  >({})
  const [actionInProgress, setActionInProgress] = React.useState<
    Record<string, boolean>
  >({})
  const [installedTerminals, setInstalledTerminals] = React.useState<
    TerminalApp[]
  >([])
  const [selectedTerminal, setSelectedTerminal] = React.useState<string>('')

  // Settings panel state
  const [_, setMfeRootState] = React.useState<string>('')
  const [showSettings, setShowSettings] = React.useState(false)
  const [mfeRootInput, setMfeRootInput] = React.useState<string>('')
  const [isSavingRoot, setIsSavingRoot] = React.useState(false)

  // Initialize configs from microFrontEnds and localStorage overrides
  React.useEffect(() => {
    const overrides = getMfeOverrides()
    const portConfigs = getPortConfigs()
    const mfeNames = Object.keys(microFrontEnds).sort()

    console.log('[MFE Dev Manager] Initializing from localStorage:', {
      overrides,
      portConfigs,
    })

    const initialConfigs: MfeConfig[] = mfeNames.map((name) => {
      const override = overrides[name]
      const portMatch = override?.match(/:(\d+)\//)
      const overridePort = portMatch ? parseInt(portMatch[1], 10) : 0

      // Use override port if exists, otherwise use saved port config, otherwise 0
      const port = overridePort || portConfigs[name] || 0

      const config = {
        name,
        prodUrl: microFrontEnds[name],
        localPort: port,
        isLocal: !!override,
      }

      if (config.isLocal) {
        console.log(`[MFE Dev Manager] ${name} is ACTIVE with port ${port}`)
      }

      return config
    })

    setConfigs(initialConfigs)
  }, [microFrontEnds])

  // Poll dev server — only when explicitly started via `npm run start:dev`.
  // REACT_APP_MFE_DEV_SERVER_ENABLED is baked in by CRA only when that script
  // injects it; plain `npm start` leaves it undefined so no requests are made.
  const devServerEnabled =
    process.env.REACT_APP_MFE_DEV_SERVER_ENABLED === 'true'

  // One-time server connectivity check on mount (no continuous polling).
  // Polling only happens for 30s after a start/stop action.
  React.useEffect(() => {
    if (!devServerEnabled) return
    let cancelled = false
    isDevServerRunning().then((running) => {
      if (cancelled) return
      setServerConnected(running)
      if (running) {
        getMfeProcesses().then((statuses) => {
          if (!cancelled) {
            setProcessStatuses(statuses)

            // Reconcile: if the dev server has no record of a running/starting
            // process for an MFE that has a localStorage override, the override
            // is stale (e.g. machine was rebooted, dev server was restarted).
            // Auto-clear those overrides so the app falls back to production URLs.
            const overrides = getMfeOverrides()
            const staleNames: string[] = []
            for (const mfeName of Object.keys(overrides)) {
              const ps = statuses[mfeName]
              const isActive =
                ps?.status === 'running' || ps?.status === 'starting'
              if (!isActive) {
                clearMfeOverride(mfeName)
                staleNames.push(mfeName)
              }
            }
            if (staleNames.length > 0) {
              console.log(
                '[MFE Dev Manager] Cleared stale overrides (process not found in dev server):',
                staleNames,
              )
              setConfigs((prev) =>
                prev.map((c) =>
                  staleNames.includes(c.name) ? { ...c, isLocal: false } : c,
                ),
              )
              setHasChanges(true)
            }
          }
        })
        getInstalledTerminals().then((terms) => {
          if (!cancelled) {
            setInstalledTerminals(terms)
            if (terms.length > 0) setSelectedTerminal(terms[0].app)
          }
        })
        getMfeConfig().then((config) => {
          if (!cancelled && config) {
            setMfeRootState(config.mfeRoot)
            setMfeRootInput(config.mfeRoot)
          }
        })
      }
    })
    return () => {
      cancelled = true
    }
  }, [devServerEnabled])

  const handlePortChange = (name: string, port: number) => {
    // Save port configuration to localStorage
    savePortConfig(name, port)
    setConfigs((prev) =>
      prev.map((config) =>
        config.name === name ? { ...config, localPort: port } : config,
      ),
    )
    onConfigChange?.()
  }

  const handleClearAll = () => {
    clearAllMfeOverrides()
    localStorage.removeItem(MFE_PORT_CONFIGS_KEY)
    setConfigs((prev) =>
      prev.map((c) => ({ ...c, isLocal: false, localPort: 0 })),
    )
    setShowActiveOnly(false) // Reset filter to show all MFEs
    setHasChanges(true)
    onConfigChange?.()
  }

  const handleSaveRoot = async () => {
    setIsSavingRoot(true)
    try {
      const result = await setMfeRoot(mfeRootInput)
      if (result.success && result.mfeRoot) {
        setMfeRootState(result.mfeRoot)
        setMfeRootInput(result.mfeRoot)
        setShowSettings(false)
      } else {
        alert(`Failed to update MFE root: ${result.error}`)
      }
    } finally {
      setIsSavingRoot(false)
    }
  }

  const getMfeFolderName = (mfeName: string): string => {
    return mfeName
      .replace(/([A-Z])/g, '-$1')
      .toLowerCase()
      .replace(/^-/, '')
  }

  // ─── Start/Stop via dev server ────────────────────────────────────────
  const handleStartMfe = async (name: string, port: number) => {
    setActionInProgress((prev) => ({ ...prev, [name]: true }))
    try {
      const result = await startMfeProcess(name, port > 0 ? port : undefined)
      if (result.success && result.data) {
        const assignedPort = result.data.port
        savePortConfig(name, assignedPort)
        setMfeOverride(name, `http://localhost:${assignedPort}/mfe.js`)
        setConfigs((prev) =>
          prev.map((c) =>
            c.name === name
              ? { ...c, localPort: assignedPort, isLocal: true }
              : c,
          ),
        )
        setHasChanges(true)
        // Update status immediately from the start response
        setProcessStatuses((prev) => ({
          ...prev,
          [name]: result.data as MfeProcessStatus,
        }))
        // Then poll every 2s for up to 30s until it reaches running/error
        pollMfeStatus(
          name,
          (status) => {
            if (status) {
              setProcessStatuses((prev) => ({ ...prev, [name]: status }))
            }
          },
          2000,
          30000,
        )
      } else {
        alert(`Failed to start ${name}: ${result.error}`)
      }
    } finally {
      setActionInProgress((prev) => ({ ...prev, [name]: false }))
    }
  }

  const handleStopMfe = async (name: string) => {
    setActionInProgress((prev) => ({ ...prev, [name]: true }))
    try {
      const result = await stopMfeProcess(name)
      if (result.success) {
        // Stop process and clear the local override
        clearMfeOverride(name)
        setConfigs((prev) =>
          prev.map((c) => (c.name === name ? { ...c, isLocal: false } : c)),
        )
        setHasChanges(true)
        setProcessStatuses((prev) => ({
          ...prev,
          [name]: { ...prev[name], status: 'stopped' },
        }))
      } else {
        alert(`Failed to stop ${name}: ${result.error}`)
      }
    } finally {
      setActionInProgress((prev) => ({ ...prev, [name]: false }))
    }
  }

  const handleOpenVSCode = async (name: string) => {
    if (serverConnected) {
      const result = await openInVSCode(name)
      if (!result.success) {
        alert(
          `Failed to open VS Code.\n\n${result.error}\n\nMake sure the "code" CLI is installed:\nVS Code → ⌘+Shift+P → "Shell Command: Install 'code' command"\n\nResolved path: ${result.path ?? 'unknown'}`,
        )
      }
    } else {
      const folder = getMfeFolderName(name)
      const command = `code ../mfe-${folder}`
      navigator.clipboard.writeText(command).then(
        () => alert(`Command copied! Run in terminal:\n${command}`),
        () => alert(`Command: ${command}`),
      )
    }
  }

  // Open the MFE folder in a new terminal window
  const handleOpenTerminal = async (name: string) => {
    if (serverConnected && selectedTerminal) {
      const result = await openInTerminal(name, selectedTerminal)
      if (!result.success) {
        alert(`Failed to open terminal.\n\n${result.error}`)
      }
    } else {
      // Fallback: copy cd command when server not running
      const folder = getMfeFolderName(name)
      const relativePath = `../mfe-${folder}`
      navigator.clipboard.writeText(`cd ${relativePath}`).then(
        () => alert(`Copied!\n\ncd ${relativePath}`),
        () => alert(`cd ${relativePath}`),
      )
    }
  }

  const isProcessRunning = (name: string): boolean => {
    const ps = processStatuses[name]
    return ps?.status === 'running' || ps?.status === 'starting'
  }

  const activeCount = configs.filter((c) => c.isLocal).length
  const runningCount = Object.values(processStatuses).filter(
    (p) => p.status === 'running' || p.status === 'starting',
  ).length

  // Filter configs by search query (search in name and URLs)
  let filteredConfigs = configs.filter((config) => {
    const query = searchQuery.toLowerCase()
    const nameMatch = config.name.toLowerCase().includes(query)
    const prodUrlMatch = config.prodUrl.toLowerCase().includes(query)
    const localUrl = `http://localhost:${config.localPort}/mfe.js`
    const localUrlMatch = localUrl.toLowerCase().includes(query)
    return nameMatch || prodUrlMatch || localUrlMatch
  })

  // Apply active-only filter if enabled
  if (showActiveOnly) {
    filteredConfigs = filteredConfigs.filter((config) => config.isLocal)
  }

  if (!isVisible) {
    return null
  }

  return (
    <div style={containerStyle}>
      <Card
        style={{
          padding: '20px',
          border: '1px solid var(--color-border-default, #d0d0d0)',
          backgroundColor: 'var(--color-background-brand-subdued, #ffffff)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Heading
              size={3}
              style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}
            >
              🛠 MFE Dev Manager
            </Heading>
            {activeCount > 0 && (
              <Input.Chip
                isChecked={showActiveOnly}
                label={` ${activeCount} active ${showActiveOnly ? '(filtered)' : ''}`}
                onChange={() => setShowActiveOnly(!showActiveOnly)}
              />
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {serverConnected && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSettings((s) => !s)}
                aria-label="Settings"
              >
                ⚙️
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleClearAll}
              disabled={activeCount === 0}
            >
              Reset All
            </Button>
          </div>
        </div>

        {/* Server Status Bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            marginBottom: '12px',
            borderRadius: '8px',
            backgroundColor: serverConnected
              ? 'var(--color-background-surface-positive-subdued, #f0fdf4)'
              : 'var(--color-background-surface-caution-subdued, #fffbeb)',
            border: `1px solid ${
              serverConnected
                ? 'var(--color-border-positive, #86efac)'
                : 'var(--color-border-caution, #fde68a)'
            }`,
            fontSize: '13px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>{serverConnected ? '🟢' : '🟡'}</span>
            <span style={{ fontWeight: 500 }}>
              {serverConnected
                ? `Dev Server connected${runningCount > 0 ? ` · ${runningCount} running` : ''}`
                : 'Dev Server not running'}
            </span>
          </div>
          {!serverConnected && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const cmd = 'node scripts/mfe-dev-server.js'
                navigator.clipboard.writeText(cmd).then(
                  () =>
                    alert(
                      `Copied! Run in a terminal from properties-ui-application:\n\n${cmd}`,
                    ),
                  () => alert(`Run this command:\n\n${cmd}`),
                )
              }}
            >
              📋 Copy Start Cmd
            </Button>
          )}
        </div>

        {/* Settings Panel */}
        {showSettings && serverConnected && (
          <div
            style={{
              padding: '12px',
              marginBottom: '12px',
              borderRadius: '8px',
              backgroundColor: 'var(--color-background-surface, #f9fafb)',
              border: '1px solid var(--color-border-default, #d0d0d0)',
            }}
          >
            <div
              style={{
                fontSize: '13px',
                fontWeight: 600,
                marginBottom: '8px',
                color: 'var(--color-text-default, #333)',
              }}
            >
              ⚙️ MFE Base Folder
            </div>
            <div
              style={{
                fontSize: '12px',
                color: 'var(--color-text-subdued, #666)',
                marginBottom: '8px',
              }}
            >
              Absolute path where all <code>mfe-*</code> repos live. Defaults to
              the parent of <code>properties-ui-application</code>.
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Input.Text
                value={mfeRootInput}
                onChange={(e: { target: { value: string } }) =>
                  setMfeRootInput(e.target.value)
                }
                placeholder="/Users/you/Projects/UI"
                style={{ flex: 1, fontFamily: 'monospace', fontSize: '12px' }}
              />
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveRoot}
                disabled={isSavingRoot || !mfeRootInput}
              >
                {isSavingRoot ? '⏳ Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        )}

        {/* Search Input */}
        <div style={{ marginBottom: '16px' }}>
          <Input.Text
            placeholder="🔍 Search by name or URL..."
            value={searchQuery}
            onChange={(e: {
              target: { value: React.SetStateAction<string> }
            }) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* MFE List */}
        <div
          style={{
            maxHeight: 'calc(80vh - 280px)',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          {filteredConfigs.length === 0 ? (
            <div
              style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: 'var(--color-text-subdued)',
              }}
            >
              {showActiveOnly && activeCount === 0
                ? 'No active MFE overrides'
                : showActiveOnly
                  ? searchQuery
                    ? `No active MFEs matching "${searchQuery}"`
                    : 'No active MFE overrides'
                  : searchQuery
                    ? `No MFEs found matching "${searchQuery}"`
                    : 'No MFEs configured'}
            </div>
          ) : (
            filteredConfigs.map((config) => {
              const ps = processStatuses[config.name]
              const running = isProcessRunning(config.name)
              const busy = actionInProgress[config.name]

              return (
                <Card
                  key={config.name}
                  style={{
                    padding: '16px',
                    border: config.isLocal
                      ? '2px solid var(--color-border-info, #0078d4)'
                      : '1px solid var(--color-border-default, #d0d0d0)',
                    backgroundColor: config.isLocal
                      ? 'var(--color-background-surface-accent-subdued, #e6f7ff)'
                      : 'var(--color-background-surface, #ffffff)',
                    boxShadow: config.isLocal
                      ? '0 2px 4px rgba(0, 120, 212, 0.1)'
                      : '0 1px 3px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  {/* MFE Name + Status */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontWeight: 600,
                        color: 'var(--color-text-default, #333)',
                      }}
                    >
                      {config.name}
                      {config.isLocal && (
                        <Badge color="primary" aria-label="local">
                          LOCAL
                        </Badge>
                      )}
                      {config.isLocal && !serverConnected && (
                        <Tooltip content="Dev server is not running — this override may be stale. The MFE may not be available at localhost. Start the dev server or click Reset All to clear.">
                          <span
                            style={{
                              fontSize: '13px',
                              cursor: 'help',
                              color: '#d97706',
                            }}
                          >
                            ⚠️ Stale?
                          </span>
                        </Tooltip>
                      )}
                    </div>
                    {serverConnected && ps && <StatusDot status={ps.status} />}
                  </div>

                  {/* URL */}
                  <div
                    style={{
                      fontSize: '12px',
                      fontFamily: 'monospace',
                      color: 'var(--color-text-subdued, #666)',
                      wordBreak: 'break-all',
                      marginBottom: '12px',
                    }}
                  >
                    {config.isLocal
                      ? `http://localhost:${config.localPort}/mfe.js`
                      : config.prodUrl}
                  </div>

                  {/* Controls: Port */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      flexWrap: 'wrap',
                      marginBottom: '12px',
                      color: 'var(--color-text-subdued, #666)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flex: 1,
                      }}
                    >
                      <span
                        style={{
                          fontSize: '13px',
                          color: 'var(--color-text-subdued)',
                        }}
                      >
                        Port:
                      </span>
                      <Input.Number
                        value={config.localPort || ''}
                        onChange={(e: { target: { value: string } }) =>
                          handlePortChange(
                            config.name,
                            parseInt(e.target.value, 10) || 0,
                          )
                        }
                        placeholder="port.."
                        min={1024}
                        max={65535}
                        style={{ width: '100px' }}
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div
                    style={{
                      display: 'flex',
                      gap: '6px',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                    }}
                  >
                    {/* Start / Stop — only when server connected */}
                    {serverConnected ? (
                      running ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleStopMfe(config.name)}
                          disabled={busy}
                        >
                          {busy ? '⏳ Stopping...' : '⏹ Stop'}
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() =>
                            handleStartMfe(config.name, config.localPort)
                          }
                          disabled={busy}
                        >
                          {busy ? '⏳ Starting...' : '▶️ Start'}
                        </Button>
                      )
                    ) : null}

                    {/* VS Code */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenVSCode(config.name)}
                    >
                      {serverConnected ? '📂 VS Code' : '📂 Open in VS Code'}
                    </Button>

                    {/* Terminal — selector + open, or fallback copy */}
                    {serverConnected && installedTerminals.length > 0 ? (
                      <div
                        style={{
                          display: 'flex',
                          gap: '4px',
                          alignItems: 'center',
                        }}
                      >
                        <select
                          value={selectedTerminal}
                          onChange={(e) => setSelectedTerminal(e.target.value)}
                          style={{
                            fontSize: '12px',
                            padding: '3px 6px',
                            borderRadius: '6px',
                            border:
                              '1px solid var(--color-border-default, #d0d0d0)',
                            background: 'var(--color-background-surface, #fff)',
                            color: 'var(--color-text-default, #333)',
                            cursor: 'pointer',
                          }}
                        >
                          {installedTerminals.map((t) => (
                            <option key={t.app} value={t.app}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenTerminal(config.name)}
                        >
                          💻 Open
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenTerminal(config.name)}
                      >
                        💻 Copy cd path
                      </Button>
                    )}

                    {/* Process error info */}
                    {ps?.error && (
                      <span
                        style={{
                          fontSize: '11px',
                          color: '#ef4444',
                          marginLeft: '4px',
                        }}
                        title={ps.error}
                      >
                        ⚠️ {ps.error.substring(0, 60)}
                      </span>
                    )}
                  </div>
                </Card>
              )
            })
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            fontSize: '12px',
            color: 'var(--color-text-subdued, #666)',
            paddingTop: '16px',
            borderTop: '1px solid var(--color-border-subtle, #e0e0e0)',
            marginTop: '16px',
          }}
        >
          {hasChanges && (
            <div
              style={{
                color: 'var(--color-text-accent)',
                marginBottom: '4px',
                fontWeight: 500,
              }}
            >
              ↻ Refresh page to apply changes
            </div>
          )}
          {!serverConnected && activeCount > 0 && (
            <div
              style={{
                padding: '8px 10px',
                marginBottom: '8px',
                borderRadius: '6px',
                backgroundColor:
                  'var(--color-background-surface-caution-subdued, #fffbeb)',
                border: '1px solid var(--color-border-caution, #fde68a)',
                color: '#92400e',
                fontWeight: 500,
              }}
            >
              ⚠️ {activeCount} override{activeCount > 1 ? 's are' : ' is'} set
              but the dev server is not running — those MFEs may not be
              available at localhost. Start the dev server (it will auto-clear
              stale overrides) or click <strong>Reset All</strong> to clear them
              now.
            </div>
          )}
          {serverConnected ? (
            <div>✅ Dev server connected — Start/Stop MFEs directly</div>
          ) : (
            <div>
              ⚡ Run{' '}
              <code
                style={{
                  background: 'var(--color-background-surface, #f5f5f5)',
                  padding: '1px 4px',
                  borderRadius: '3px',
                  fontSize: '11px',
                }}
              >
                node scripts/mfe-dev-server.js
              </code>{' '}
              for Start/Stop controls
            </div>
          )}
          <div style={{ marginTop: '4px' }}>
            💾 Port configs & overrides persist across reloads
          </div>
        </div>
      </Card>
    </div>
  )
}
